import { useDevices, useFleets } from '@/api/devices'
import { useTenants } from '@/api/admin'
import type { DeviceStatus } from '@/types'

interface Props {
  tenantId: string | null
}

const STATUS_ORDER: DeviceStatus[] = ['active', 'provisioning', 'pending', 'maintenance', 'failed', 'decommissioned']

export function Dashboard({ tenantId }: Props) {
  const { data: devicesResult } = useDevices(tenantId)
  const { data: fleetsResult } = useFleets(tenantId)
  const { data: tenantsResult } = useTenants()

  const devices = devicesResult?.data ?? []
  const total = devicesResult?.total ?? 0

  const byStatus = STATUS_ORDER.map(status => ({
    status,
    count: devices.filter(d => d.status === status).length,
  }))

  const activeCount = devices.filter(d => d.status === 'active').length
  const failedCount = devices.filter(d => d.status === 'failed').length
  const fleetCount = fleetsResult?.total ?? 0

  const tenantSlug = tenantsResult?.data?.find(t => t.id === tenantId)?.slug

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {tenantSlug ? `Fleet overview — ${tenantSlug}` : tenantId ? 'Fleet overview' : 'Select a tenant to view data'}
          </p>
        </div>
      </div>

      <div className="cards-grid">
        <div className="stat-card">
          <div className="stat-label">Total Devices</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value">{activeCount}</div>
        </div>
        <div className="stat-card stat-card--neutral">
          <div className="stat-label">Fleets</div>
          <div className="stat-value">{fleetCount}</div>
        </div>
        <div className={`stat-card${failedCount > 0 ? ' stat-card--danger' : ' stat-card--neutral'}`}>
          <div className="stat-label">Failed</div>
          <div className="stat-value">{failedCount}</div>
        </div>
      </div>

      {!tenantId && (
        <div className="empty-state">Select a tenant to view data.</div>
      )}

      {tenantId && devices.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Count</th>
                <th>% of fleet</th>
              </tr>
            </thead>
            <tbody>
              {byStatus.filter(r => r.count > 0).map(({ status, count }) => (
                <tr key={status}>
                  <td><span className={`badge badge-${status}`}>{status}</span></td>
                  <td>{count}</td>
                  <td>{total > 0 ? ((count / total) * 100).toFixed(1) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
